import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Maximize2, Minimize2, Save, ExternalLink } from "lucide-react";
import { EntityLockButton } from "@/components/EntityLockButton";
import { DiscussDocumentButton } from "@/components/DiscussDocumentButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { safeDate } from "@/lib/safeDate";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { RichTextEditor } from "./RichTextEditor";
import { LinkedDocuments } from "./LinkedDocuments";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { knowledgeBaseItemQueryOptions } from "@/queries/knowledgeBaseDocuments";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { sourcesService } from "@/services/sourcesService";

const isHtml = (str: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(str);
};

const ensureHtmlContent = (content: string | null | undefined): string => {
  if (!content) return "";
  if (isHtml(content)) return content;
  try {
    const html = marked.parse(content, { async: false }) as string;
    return DOMPurify.sanitize(html);
  } catch (error) {
    const escaped = content
      .split('\n\n')
      .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');
    return escaped;
  }
};

interface DocumentViewerProps {
  itemId: string;
  itemType: "document" | "source";
  onClose: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export function DocumentViewer({ 
  itemId, 
  itemType, 
  onClose, 
  isExpanded, 
  onToggleExpand 
}: DocumentViewerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { pendingClip, clearPendingClip } = useDocumentViewer();
  const [isEditing, setIsEditing] = useState(true); // Auto-start in edit mode
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);
  const [titlePromptOpen, setTitlePromptOpen] = useState(false);
  const [promptedTitle, setPromptedTitle] = useState("");
  const [pendingClipContent, setPendingClipContent] = useState<string | null>(null);

  const docQuery = useQuery({
    ...knowledgeBaseItemQueryOptions(itemId || "__none__"),
    enabled: itemType === "document" && !!itemId,
  });
  const sourceQuery = useQuery({
    queryKey: ["source", itemId],
    queryFn: async () => sourcesService.getSource(itemId),
    enabled: itemType === "source" && !!itemId,
  });
  const item = itemType === "document" ? docQuery.data : sourceQuery.data;
  const isLoading = itemType === "document" ? docQuery.isLoading : sourceQuery.isLoading;

  // Reset initialization when item changes
  useEffect(() => {
    setHasInitialized(false);
    setIsEditing(true);
  }, [itemId]);

  // Auto-populate edit fields when item loads (for auto-edit mode)
  useEffect(() => {
    if (item && !hasInitialized) {
      setEditedTitle(item.title || "");
      setEditedContent(ensureHtmlContent(item.content));
      setHasInitialized(true);
    }
  }, [item, hasInitialized]);

  useEffect(() => {
    if (pendingClip && item && itemType === "document") {
      const newContent = item.content 
        ? `${item.content}\n\n---\n\n${pendingClip}`
        : pendingClip;
      
      clearPendingClip();
      
      if (!item.title || item.title.trim() === "") {
        setPendingClipContent(newContent);
        setPromptedTitle("");
        setTitlePromptOpen(true);
      } else {
        setEditedTitle(item.title);
        setEditedContent(newContent);
        setIsEditing(true);
        
        const saveClippedContent = async () => {
          try {
            await knowledgeBaseService.updateKnowledgeBaseItem(itemId, {
              title: item.title,
              content: newContent,
            });
            
            queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase-item", itemId] });
            
            toast({
              title: "Content clipped and saved",
              description: "The selected text has been added and saved to the document.",
            });
          } catch (error) {
            toast({
              title: "Content clipped",
              description: "Content added but failed to auto-save. Please save manually.",
              variant: "destructive",
            });
          }
        };
        
        saveClippedContent();
      }
    }
  }, [pendingClip, item, itemType, itemId, clearPendingClip, toast, queryClient]);

  const handleTitlePromptSave = async () => {
    if (!promptedTitle.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for the document.",
        variant: "destructive",
      });
      return;
    }

    if (!pendingClipContent) return;

    setEditedTitle(promptedTitle);
    setEditedContent(pendingClipContent);
    setIsEditing(true);
    setTitlePromptOpen(false);

    try {
      await knowledgeBaseService.updateKnowledgeBaseItem(itemId, {
        title: promptedTitle,
        content: pendingClipContent,
      });
      
      queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-item", itemId] });
      
      toast({
        title: "Content clipped and saved",
        description: "The selected text has been added and saved to the document.",
      });
    } catch (error) {
      toast({
        title: "Content clipped",
        description: "Content added but failed to auto-save. Please save manually.",
        variant: "destructive",
      });
    }

    setPendingClipContent(null);
    setPromptedTitle("");
  };

  const handleTitlePromptCancel = () => {
    setTitlePromptOpen(false);
    setPendingClipContent(null);
    setPromptedTitle("");
    toast({
      title: "Clip cancelled",
      description: "The content was not added to the document.",
    });
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      const data = {
        title: editedTitle,
        content: editedContent,
      };
      
      if (itemType === "document") {
        await knowledgeBaseService.updateKnowledgeBaseItem(itemId, data);
      } else {
        await sourcesService.updateSource(itemId, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: itemType === "document" ? ["knowledgebase"] : ["sources"] 
      });
      queryClient.invalidateQueries({ 
        queryKey: itemType === "document" 
          ? ["knowledgebase-item", itemId] 
          : ["source", itemId]
      });
      toast({
        title: "Saved",
        description: `${itemType === "document" ? "Document" : "Source"} updated successfully`,
      });
      setIsEditing(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    },
  });

  const handleEdit = () => {
    if (item) {
      setEditedTitle(item.title || "");
      setEditedContent(ensureHtmlContent(item.content));
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedTitle("");
    setEditedContent("");
  };

  const containerClass = isExpanded 
    ? 'fixed inset-0 z-50 bg-background flex flex-col border-l border-border' 
    : 'flex flex-col h-full min-h-0 bg-sidebar';
  
  const containerStyle = isExpanded 
    ? { left: 'var(--sidebar-width, 20rem)' } 
    : {};

  if (isLoading) {
    return (
      <div className={containerClass} style={containerStyle}>
        <div className="flex items-center justify-center flex-1 min-h-0">
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className={containerClass} style={containerStyle}>
        <div className="flex items-center justify-center flex-1 min-h-0">
          <p className="text-sm text-muted-foreground">Item not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass} style={containerStyle}>
      <div className="p-3 border-b flex items-center justify-between bg-sidebar shrink-0 border-l">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-medium">Document</span>
          {item.googleDriveUrl && (
            <a 
              href={item.googleDriveUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Badge variant="outline" className="text-xs flex items-center gap-1 hover-elevate">
                <ExternalLink className="h-3 w-3" />
                Google Drive
              </Badge>
            </a>
          )}
        </div>
        <div className="flex items-center gap-1">
          <DiscussDocumentButton
            itemId={itemId}
            itemType={itemType}
            title={item.title}
            contentHtml={item.content}
          />
          {itemType === "document" && (
            <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
              <EntityLockButton
                entityType="document"
                entityId={itemId}
                isLocked={item?.isLocked === true}
                size="sm"
                customTitle={item?.isLocked ? "Unlock document" : "Lock document"}
              />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleExpand}
            data-testid="button-toggle-expand-document"
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-document-viewer"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-background">
        <div className="p-6 max-w-4xl mx-auto">
          {isEditing ? (
            <div className="flex flex-col gap-4">
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-2xl font-bold border-0 px-0 focus-visible:ring-0"
                placeholder="Document title..."
                data-testid="input-edit-document-title"
              />
              <RichTextEditor
                content={editedContent}
                onChange={setEditedContent}
                placeholder="Start writing..."
              />
              <div className="flex gap-2 pt-4 border-t sticky bottom-0 bg-background py-4">
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="button-save-document-edit"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                  data-testid="button-cancel-document-edit"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-2xl font-bold">{item.title}</h1>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  data-testid="button-edit-document"
                >
                  Edit
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  Last updated {(() => {
                    const updated = safeDate(item.updatedAt || item.createdAt);
                    return updated ? formatDistanceToNow(updated, { addSuffix: true }) : "unknown";
                  })()}
                </span>
              </div>
              {item.url && (
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={item.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate"
                    data-testid="link-document-url"
                  >
                    {item.url}
                  </a>
                </div>
              )}
              <div 
                className="prose prose-sm dark:prose-invert max-w-none pt-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content || '') }}
              />
              
              {itemType === "document" && (
                <LinkedDocuments documentId={itemId} className="mt-6" />
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={titlePromptOpen} onOpenChange={setTitlePromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name this document</DialogTitle>
            <DialogDescription>
              This document needs a title before saving clipped content.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="document-title">Document Title</Label>
            <Input
              id="document-title"
              value={promptedTitle}
              onChange={(e) => setPromptedTitle(e.target.value)}
              placeholder="Enter a title..."
              className="mt-2"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleTitlePromptSave();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleTitlePromptCancel}>
              Cancel
            </Button>
            <Button onClick={handleTitlePromptSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
